import { parseMoeda } from "@/utils/money";
import type { KanbanCard } from "@/types/kanban";
import type { StatusNegociacaoFreelancer } from "@/types/kanban";
import { externalUrlHref } from "@/utils/external-url";

const STATUS_ICON: Record<StatusNegociacaoFreelancer, string> = {
  topou: "✅",
  contra_proposta_aceita: "✅",
  oferta_feita: "⌛",
  aguardando_resposta_agencia: "⌛",
  contra_proposta: "💬",
  recusou: "❌",
  desistiu: "❌",
};

const STATUS_LABEL: Record<StatusNegociacaoFreelancer, string> = {
  topou: "Topou",
  contra_proposta_aceita: "Contraproposta aceita",
  oferta_feita: "Oferta feita",
  aguardando_resposta_agencia: "Aguardando agência",
  contra_proposta: "Contraproposta",
  recusou: "Recusou",
  desistiu: "Desistiu",
};

/** Rótulo curto do status de negociação (ex.: para badges no card). */
export function negociacaoStatusLabel(status: StatusNegociacaoFreelancer): string {
  return STATUS_LABEL[status] ?? status;
}

/** Ícone (emoji) do status de negociação. */
export function negociacaoStatusIcon(status: StatusNegociacaoFreelancer): string {
  return STATUS_ICON[status] ?? "";
}

const formatValorCentavos = (centavos: number | null | undefined, moeda: string | null | undefined): string | null => {
  if (centavos == null) return null;
  const reais = centavos / 100;
  if (!Number.isFinite(reais) || reais <= 0) return null;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: parseMoeda(moeda),
    maximumFractionDigits: 0,
  }).format(reais);
};

/**
 * Gera texto consolidado das negociações atuais de uma lista de cards (etapa).
 * Formato pensado pra colar em Slack/e-mail no padrão que o time já usa.
 *
 * Inclui apenas cards com `negociacao_atual` definido — quem não tem
 * negociação registrada é omitido para não poluir o texto.
 */
export function formatStatusEtapaText(
  etapaNome: string,
  cards: KanbanCard[],
  moeda?: string | null,
): string {
  const linhas: string[] = [`✨ Status ${etapaNome}:`];
  for (const card of cards) {
    const neg = card.negociacao_atual;
    if (!neg) continue;
    const icon = STATUS_ICON[neg.status];
    const label = STATUS_LABEL[neg.status];
    const valor = formatValorCentavos(neg.valor, moeda);
    const partes: string[] = [
      `${icon} ${card.freelancer.nome}`,
      valor ? `· ${valor}` : null,
      `· ${label}`,
      neg.observacao ? `(${neg.observacao})` : null,
    ].filter((p): p is string => !!p);
    const url = externalUrlHref(card.freelancer.portfolio_url ?? card.freelancer.linkedin_url);
    linhas.push(`• ${partes.join(" ")}${url ? `\n  ${url}` : ""}`);
  }
  return linhas.join("\n");
}

/** Conta quantos cards da etapa têm negociação registrada. */
export function countCardsComNegociacao(cards: KanbanCard[]): number {
  return cards.filter((c) => !!c.negociacao_atual).length;
}
